import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  IndeterminateCheckbox,
  type PermissionBulkCheckboxConfig,
  type PermissionGlobalBulkConfig,
} from './CollapsiblePermissionGroups'
import {type PermissionCatalogEntry} from '../core/permissionGrouping'
import {buildProductionUIModules, type ProductionUIModuleSection} from '../core/productionPermissionGrouping'

const norm = (c: string) => c.trim().toUpperCase()

type Props<T extends PermissionCatalogEntry> = {
  catalog: T[]
  searchQuery: string
  getItemKey: (item: T) => string
  renderItem: (item: T) => React.ReactNode
  emptyMessage?: string
  startAllExpanded?: boolean
  bulkCheckbox?: PermissionBulkCheckboxConfig<T>
  globalBulk?: PermissionGlobalBulkConfig
  filter?: (e: T) => boolean
  extraSearch?: (e: T, queryUpper: string) => boolean
}

export function ProductionGroupedPermissionAccordion<T extends PermissionCatalogEntry>({
  catalog,
  searchQuery,
  getItemKey,
  renderItem,
  emptyMessage = 'No items match.',
  startAllExpanded = false,
  bulkCheckbox,
  globalBulk,
  filter,
  extraSearch,
}: Props<T>) {
  const modules = useMemo(
    () => buildProductionUIModules<T>(catalog, {searchQuery, filter, extraSearch}),
    [catalog, searchQuery, filter, extraSearch]
  )

  const modulesFingerprint = useMemo(
    () => modules.map((m) => `${m.moduleLabel}:${m.sections.length}`).join('|'),
    [modules]
  )

  const modulesRef = useRef(modules)
  modulesRef.current = modules

  const [expandedMods, setExpandedMods] = useState<Set<string>>(() => new Set())

  useEffect(() => {
    const ms = modulesRef.current
    const q = searchQuery.trim()
    if (!q) {
      setExpandedMods(startAllExpanded ? new Set(ms.map((m) => m.moduleLabel)) : new Set())
      return
    }
    setExpandedMods(new Set(ms.map((m) => m.moduleLabel)))
  }, [searchQuery, modulesFingerprint, startAllExpanded])

  const toggleMod = useCallback((k: string) => {
    setExpandedMods((prev) => {
      const n = new Set(prev)
      if (n.has(k)) n.delete(k)
      else n.add(k)
      return n
    })
  }, [])

  const allVisibleCodes = useMemo(() => {
    if (!bulkCheckbox) return [] as string[]
    const codes: string[] = []
    const seen = new Set<string>()
    for (const mod of modules) {
      for (const sec of mod.sections) {
        for (const item of sec.items) {
          const c = norm(bulkCheckbox.getItemCode(item))
          if (c && !seen.has(c)) {
            seen.add(c)
            codes.push(c)
          }
        }
      }
    }
    return codes
  }, [modules, bulkCheckbox])

  const visibleSelectedCount = useMemo(() => {
    if (!bulkCheckbox) return 0
    return allVisibleCodes.filter((c) => bulkCheckbox.isSelected(c)).length
  }, [allVisibleCodes, bulkCheckbox])

  const allVisibleSelected =
    allVisibleCodes.length > 0 && visibleSelectedCount === allVisibleCodes.length
  const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected

  const toggleGlobalBulk = useCallback(() => {
    if (!bulkCheckbox || bulkCheckbox.disabled) return
    bulkCheckbox.onSetMany(allVisibleCodes, !allVisibleSelected)
  }, [bulkCheckbox, allVisibleCodes, allVisibleSelected])

  const applyVisibleSelectAll = useCallback(() => {
    if (!bulkCheckbox || bulkCheckbox.disabled) return
    bulkCheckbox.onSetMany(allVisibleCodes, true)
  }, [bulkCheckbox, allVisibleCodes])

  const applyVisibleUnselectAll = useCallback(() => {
    if (!bulkCheckbox || bulkCheckbox.disabled) return
    bulkCheckbox.onSetMany(allVisibleCodes, false)
  }, [bulkCheckbox, allVisibleCodes])

  const bulkScopeDescription = useMemo(() => {
    const q = searchQuery.trim()
    if (!q || !bulkCheckbox) return null
    return `Bulk action applies to filtered results (${allVisibleCodes.length} visible).`
  }, [searchQuery, allVisibleCodes.length, bulkCheckbox])

  const totalItems = useMemo(
    () =>
      modules.reduce((s, mod) => s + mod.sections.reduce((t, sec) => t + sec.items.length, 0), 0),
    [modules]
  )

  if (totalItems === 0) {
    return (
      <div className='text-center text-muted py-5 border rounded bg-light'>{emptyMessage}</div>
    )
  }

  return (
    <div className='d-flex flex-column gap-2'>
      {bulkCheckbox && globalBulk ? (
        <div className='d-flex flex-wrap align-items-center gap-2 gap-md-3 py-2 px-2 px-md-3 border rounded bg-light'>
          <div className='d-flex align-items-center gap-2'>
            <IndeterminateCheckbox
              id='perm-bulk-global-prod'
              checked={allVisibleSelected}
              indeterminate={someVisibleSelected}
              disabled={globalBulk.disabled || bulkCheckbox.disabled}
              title={
                searchQuery.trim()
                  ? 'Select or clear all permissions currently visible (filtered list)'
                  : 'Select or clear all permissions in the list'
              }
              onChange={toggleGlobalBulk}
            />
            <span className='small text-gray-800'>
              <span className='fw-semibold text-gray-900'>
                {globalBulk.selectedGlobalCount} / {globalBulk.totalCatalogCount}
              </span>
              <span className='text-muted'> {globalBulk.legend}</span>
            </span>
          </div>
          <div className='d-flex flex-wrap align-items-center gap-1'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              disabled={globalBulk.disabled || bulkCheckbox.disabled || allVisibleCodes.length === 0}
              onClick={applyVisibleSelectAll}
            >
              Select all
            </button>
            <button
              type='button'
              className='btn btn-sm btn-light'
              disabled={globalBulk.disabled || bulkCheckbox.disabled || allVisibleCodes.length === 0}
              onClick={applyVisibleUnselectAll}
            >
              Unselect all
            </button>
          </div>
          {bulkScopeDescription ? (
            <span className='small text-primary ms-md-2' role='note'>
              {bulkScopeDescription}
            </span>
          ) : null}
        </div>
      ) : null}

      {modules.map((mod) => (
        <ProductionModuleSection
          key={mod.moduleLabel}
          mod={mod}
          expanded={expandedMods.has(mod.moduleLabel)}
          onToggleExpand={() => toggleMod(mod.moduleLabel)}
          getItemKey={getItemKey}
          renderItem={renderItem}
          bulkCheckbox={bulkCheckbox}
        />
      ))}
    </div>
  )
}

function ProductionModuleSection<T extends PermissionCatalogEntry>({
  mod,
  expanded,
  onToggleExpand,
  getItemKey,
  renderItem,
  bulkCheckbox,
}: {
  mod: ProductionUIModuleSection<T>
  expanded: boolean
  onToggleExpand: () => void
  getItemKey: (item: T) => string
  renderItem: (item: T) => React.ReactNode
  bulkCheckbox?: PermissionBulkCheckboxConfig<T>
}) {
  const moduleCodes = useMemo(() => {
    const codes: string[] = []
    const seen = new Set<string>()
    for (const sec of mod.sections) {
      for (const item of sec.items) {
        if (!bulkCheckbox) continue
        const c = norm(bulkCheckbox.getItemCode(item))
        if (c && !seen.has(c)) {
          seen.add(c)
          codes.push(c)
        }
      }
    }
    return codes
  }, [mod.sections, bulkCheckbox])

  const moduleSelected = bulkCheckbox
    ? moduleCodes.filter((c) => bulkCheckbox.isSelected(c)).length
    : 0
  const allModuleSelected = moduleCodes.length > 0 && moduleSelected === moduleCodes.length
  const someModuleSelected = moduleSelected > 0 && !allModuleSelected

  const toggleModuleBulk = () => {
    if (!bulkCheckbox || bulkCheckbox.disabled) return
    bulkCheckbox.onSetMany(moduleCodes, !allModuleSelected)
  }

  const selectModule = () => {
    if (!bulkCheckbox || bulkCheckbox.disabled) return
    bulkCheckbox.onSetMany(moduleCodes, true)
  }

  const clearModule = () => {
    if (!bulkCheckbox || bulkCheckbox.disabled) return
    bulkCheckbox.onSetMany(moduleCodes, false)
  }

  const idBase = `prod-mod-${mod.moduleLabel.replace(/\s+/g, '-').toLowerCase()}`
  const totalRows = mod.sections.reduce((s, x) => s + x.items.length, 0)

  return (
    <div className='border rounded overflow-hidden bg-white'>
      <div className='d-flex flex-wrap align-items-stretch'>
        <button
          type='button'
          className='btn btn-light d-flex align-items-center gap-2 flex-grow-1 text-start py-2 px-3 border-0 rounded-0'
          style={{minWidth: '8rem'}}
          id={`${idBase}-hdr`}
          aria-expanded={expanded}
          aria-controls={`${idBase}-body`}
          onClick={onToggleExpand}
        >
          <span className='text-gray-500 flex-shrink-0' aria-hidden>
            {expanded ? '▾' : '▸'}
          </span>
          <span className='d-flex flex-wrap align-items-baseline gap-1'>
            <span className='fw-bold text-gray-900'>{mod.moduleLabel}</span>
            <span className='small text-muted fw-normal'>
              — {totalRows} permission{totalRows === 1 ? '' : 's'}
            </span>
          </span>
        </button>
        {bulkCheckbox ? (
          <div
            className='d-flex flex-wrap align-items-center gap-2 py-2 px-3 border-top border-md-0 border-md-start bg-white'
            onClick={(e) => e.stopPropagation()}
            role='presentation'
          >
            <div className='d-flex justify-content-center align-items-center flex-shrink-0' style={{width: '2.5rem'}}>
              <IndeterminateCheckbox
                id={`${idBase}-mod-cb`}
                checked={allModuleSelected}
                indeterminate={someModuleSelected}
                disabled={bulkCheckbox.disabled}
                style={{width: '1.05rem', height: '1.05rem'}}
                title='Select or clear all permissions in this module'
                onChange={toggleModuleBulk}
              />
            </div>
            <button type='button' className='btn btn-link btn-sm py-0 px-1' onClick={selectModule}>
              Select module
            </button>
            <button type='button' className='btn btn-link btn-sm py-0 px-1 text-muted' onClick={clearModule}>
              Clear
            </button>
            <span className='small text-muted'>
              {moduleSelected} / {moduleCodes.length} {bulkCheckbox.moduleCountLegend}
            </span>
          </div>
        ) : null}
      </div>
      {expanded ? (
        <div id={`${idBase}-body`} role='region' aria-labelledby={`${idBase}-hdr`}>
          {mod.sections.map((sec) => (
            <div key={sec.sectionKey} className='border-top'>
              <div className='px-3 py-2 fw-semibold small text-gray-800 bg-light d-flex flex-wrap gap-2 align-items-baseline'>
                <span>{sec.sectionTitle}</span>
                {sec.subtitle ? <span className='text-muted fw-normal'>· {sec.subtitle}</span> : null}
              </div>
              {sec.items.map((item) => (
                <React.Fragment key={getItemKey(item)}>{renderItem(item)}</React.Fragment>
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
